import React, { Fragment, useEffect, useState } from 'react';
import Button from 'react-bootstrap-button-loader';
import Permisos from './permisos';
import notie from 'notie';
import '../../assets/css/notie.css';
import { PERMISOS_CONFIG, getDefaultPermisosByTipo } from './permisos.config';
import { useUserPermisos } from '../hooks/useUserPermisos';

const initialFormState = {
  id: null,
  nombres: '',
  apellidos: '',
  correo: '',
  telefono: '',
  tipo: 'Limitado',
  tipo_usuario: 2,
  activo: 1,
  estado: 'Activo',
  contrasena: ''
};

const normalizarPermisos = (data = {}) => {
  const normalizados = {};
  PERMISOS_CONFIG.forEach(({ key }) => {
    const value = data[key];
    if (typeof value === 'boolean') {
      normalizados[key] = value;
    } else if (value && typeof value === 'object') {
      normalizados[key] = Object.values(value).some(Boolean);
    } else {
      normalizados[key] = false;
    }
  });
  return normalizados;
};

const obtenerPermisosIniciales = () => normalizarPermisos();

const ActualizarUsuario = ({ pag_actualizar, setPag_actualizar, onUsuarioActualizado }) => {

  const [form, setForm] = useState(initialFormState);
  const [permisos, setPermisos] = useState(obtenerPermisosIniciales);
  const [loading, setLoading] = useState(false);
  const [cargando, setCargando] = useState(false);
  const { usuario: usuarioActual } = useUserPermisos();
  const esSuperAdminActual = usuarioActual?.tipo_usuario === 3;
  const puedeVerContrasena = esSuperAdminActual;
  const puedeGestionarPermisos = esSuperAdminActual;

  useEffect(() => {
    if (!pag_actualizar) {
      setForm(initialFormState);
      setPermisos(obtenerPermisosIniciales());
      return;
    }

    setCargando(true);
    fetch(`/p_usuarios/${pag_actualizar}`)
      .then(async res => {
        if (!res.ok) {
          throw new Error('No se pudo obtener el usuario');
        }
        const payload = await res.json();
        return payload?.resultado ? payload.resultado : payload;
      })
      .then(data => {
        setForm({
          id: data.id || pag_actualizar,
          nombres: data.nombres || '',
          apellidos: data.apellidos || '',
          correo: data.correo || '',
          telefono: data.telefono || '',
          tipo: data.tipo || 'Limitado',
          tipo_usuario: data.tipo_usuario,
          activo: typeof data.activo === 'number' ? data.activo : 1,
          estado: data.estado || 'Activo',
          contrasena: data.contrasena || ''
        });
        setPermisos(normalizarPermisos(data.permisos || {}));
      })
      .catch(error => {
        console.error('Error cargando usuario:', error);
        notie.alert({ type: 'error', text: 'No se pudo cargar la información del usuario', time: 3 });
      })
      .finally(() => setCargando(false));
  }, [pag_actualizar]);

  const tipoToNumero = (tipo = '') => {
    const normalized = String(tipo || '').trim().toLowerCase();
    if (normalized === 'superadministrador') return 3;
    if (normalized === 'administrador') return 1;
    return 2;
  };

  const onChange = e => {
    const { name, value } = e.target;
    if (name === 'tipo') {
      if (!esSuperAdminActual) {
        return;
      }
      setForm(prev => ({ ...prev, tipo: value, tipo_usuario: tipoToNumero(value) }));
      setPermisos(getDefaultPermisosByTipo(value));
      return;
    }
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const guardar = () => {
    if (!form.nombres || !form.correo) {
      notie.alert({ type: 'warning', text: 'Nombre y correo son obligatorios', time: 3 });
      return;
    }

    setLoading(true);
    fetch(`/p_usuarios/${pag_actualizar}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...form, permisos })
    })
      .then(async res => {
        const payload = await res.json().catch(() => ({}));
        if (!res.ok || (payload && payload.estado !== 1)) {
          throw new Error(payload?.resultado || 'No se pudo actualizar el usuario');
        }

        if (typeof onUsuarioActualizado === 'function') {
          onUsuarioActualizado();
        } else {
          setPag_actualizar(0);
        }
        notie.alert({ type: 'success', text: 'Usuario actualizado correctamente', time: 2 });
      })
      .catch(error => {
        console.error('Error al actualizar usuario:', error);
        notie.alert({ type: 'error', text: error.message || 'No se pudo actualizar el usuario', time: 3 });
      })
      .finally(() => setLoading(false));
  };

  const isDisabled = cargando || !pag_actualizar;

  return (
    <Fragment>

      <div className="container-fluid">
        <div className="card">
          <div className="card-body">

            <button
              className="btn btn-outline-light-2x txt-dark mb-4"
              onClick={() => setPag_actualizar(0)}
              disabled={loading}
            >
              <i className="icofont icofont-arrow-left" />Regresar
            </button>

            <div className="row">
              <div className="col-12 pb-4">
                <h4><strong>Editar Usuario</strong></h4>
              </div>

              <div className="col-md-6">
                <label>Nombres</label>
                <input
                  className="form-control"
                  name="nombres"
                  value={form.nombres}
                  onChange={onChange}
                  disabled={isDisabled}
                />
              </div>

              <div className="col-md-6">
                <label>Apellidos</label>
                <input
                  className="form-control"
                  name="apellidos"
                  value={form.apellidos}
                  onChange={onChange}
                  disabled={isDisabled}
                />
              </div>

              <div className="col-md-6 mt-3">
                <label>Correo</label>
                <input
                  className="form-control"
                  type="email"
                  name="correo"
                  value={form.correo}
                  onChange={onChange}
                  disabled={isDisabled}
                />
              </div>

              <div className="col-md-6 mt-3">
                <label>Teléfono</label>
                <input
                  className="form-control"
                  name="telefono"
                  value={form.telefono}
                  onChange={onChange}
                  disabled={isDisabled}
                />
              </div>

              {puedeVerContrasena && (
                <div className="col-md-6 mt-3">
                  <label>Contraseña (solo lectura)</label>
                  <input
                    className="form-control"
                    name="contrasena"
                    value={form.contrasena || ''}
                    readOnly
                  />
                </div>
              )}

              <div className="col-md-6 mt-3">
                <label>Tipo Usuario</label>
                <select
                  className="form-control"
                  name="tipo"
                  value={form.tipo}
                  onChange={onChange}
                  disabled={isDisabled || !esSuperAdminActual}
                >
                  <option value="Administrador">Administrador</option>
                  <option value="Limitado">Limitado</option>
                  {(esSuperAdminActual || form.tipo === 'SuperAdministrador') && (
                    <option value="SuperAdministrador">Super Administrador</option>
                  )}
                </select>
              </div>
            </div>

            {puedeGestionarPermisos && (
              <Permisos permisos={permisos} onChange={setPermisos} />
            )}

            <div className='mt-4'>
              <div className="d-flex justify-content-end mt-4">

                <Button className="btn btn-primary" onClick={guardar} loading={loading} disabled={isDisabled}>
                  Guardar Cambios
                </Button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ActualizarUsuario;
